import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/layout/Navbar';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {!submitted ? (
              <>
                <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>{t('auth.forgotPassword.title') || 'Forgot Password?'}</CardTitle>
                <CardDescription>
                  {t('auth.forgotPassword.description') || "Enter your email and we'll send you a reset link"}
                </CardDescription>
              </>
            ) : (
              <>
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <CardTitle className="text-green-600">{t('auth.forgotPassword.sent') || 'Check Your Email'}</CardTitle>
                <CardDescription>
                  {t('auth.forgotPassword.sentDescription') || 'If an account exists with this email, you will receive a password reset link.'}
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.login.email') || 'Email'}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.loading') || 'Loading...'}
                    </>
                  ) : (
                    t('auth.forgotPassword.submit') || 'Send Reset Link'
                  )}
                </Button>
              </form>
            ) : (
              <Button variant="outline" className="w-full" asChild>
                <Link to="/login">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('auth.forgotPassword.backToLogin') || 'Back to Login'}
                </Link>
              </Button>
            )}
            
            {!submitted && (
              <div className="mt-4 text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
                  <ArrowLeft className="inline h-4 w-4 mr-1" />
                  {t('auth.forgotPassword.backToLogin') || 'Back to Login'}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
